import { useState, useEffect } from "react";
import { Menu, X, Search, User, LogOut, Sparkles, ShieldCheck, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SignUpDialog from "@/components/SignUpDialog";
import UserAvatarMenu from "@/components/profile/UserAvatarMenu";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import NotificationBell from "@/components/notifications/NotificationBell";
import logo from "@/assets/logo.png";
import { useSeason } from "@/components/seasonal/SeasonalThemeProvider";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

const navLinks = [
  { label: "Ideas", href: "/ideas" },
  { label: "Opportunities", href: "/opportunities" },
  { label: "Research & Insights", href: "/insights" },
  { label: "Events", href: "/events" },
];

type SiteHeaderProps = {
  variant?: "hero" | "solid";
};

const SiteHeader = ({ variant = "solid" }: SiteHeaderProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, signOut } = useAuth();
  const { isPartnerAdmin, isSuperAdmin } = useRoles();
  const partnerHubOn = useFeatureFlag("partnerHub");
  const coachOn = useFeatureFlag("coach");
  const { season } = useSeason();
  const isOwnerAdmin = isSuperAdmin && user?.email?.toLowerCase() === "emergingmrkt@gmail.com";

  useEffect(() => {
    if (variant !== "hero") return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  const overHero = variant === "hero" && !scrolled;
  const linkClass = overHero
    ? "text-sm text-hero-fg hover:opacity-80 transition-colors"
    : "text-sm text-muted-foreground hover:text-foreground transition-colors";

  return (
    <>
    <SignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
      overHero
        ? "bg-transparent border-b border-transparent"
        : "bg-background/85 backdrop-blur-xl border-b border-border"
    }`}>
      <div className="container mx-auto px-6 flex items-center h-16 gap-4">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-6 shrink-0">
          <a href="/" className={`flex items-center gap-2 font-display text-xl font-bold tracking-tight ${overHero ? "text-hero-fg" : "text-foreground"}`}>
            <img src={logo} alt="emrkt logo" className="h-8 w-8" />
            <span>emrkt</span>
          </a>
          <nav className="hidden md:flex items-center gap-5">
            {navLinks.map((link, i) => (
              <span key={link.label} className="flex items-center gap-5">
                {i > 0 && <span className={`w-1 h-1 rounded-full ${overHero ? "bg-hero-muted" : "bg-muted-foreground"} opacity-50`} />}
                <a href={link.href} className={linkClass}>
                  {link.label}
                </a>
              </span>
            ))}
            {user && isPartnerAdmin && partnerHubOn && (
              <span className="flex items-center gap-5">
                <span className={`w-1 h-1 rounded-full ${overHero ? "bg-hero-muted" : "bg-muted-foreground"} opacity-50`} />
                <a href="/partner" className={linkClass}>Partner Hub</a>
              </span>
            )}
          </nav>
        </div>

        {/* Center: Search */}
        <div className="hidden lg:flex flex-1 justify-center max-w-sm mx-auto">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search…"
              className={`pl-9 h-9 rounded-full ${overHero ? "bg-white/10 border-white/20 text-hero-fg placeholder:text-hero-muted" : "bg-muted/60 border-border/60"}`}
            />
          </div>
        </div>

        {/* Right: Auth buttons */}
        <div className="hidden md:flex items-center gap-3 shrink-0 ml-auto">
          {user ? (
            <>
              <NotificationBell />
              <UserAvatarMenu />
            </>
          ) : (
            <>
              <a href="/auth">
                <Button variant="ghost" size="sm" className={overHero ? "text-hero-fg/90 hover:text-hero-fg hover:bg-white/10" : "text-muted-foreground"}>Sign In</Button>
              </a>
              <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full px-4" onClick={() => setSignUpOpen(true)}>
                Get Started
              </Button>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button className={`md:hidden ml-auto ${overHero ? "text-hero-fg" : "text-foreground"}`} onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-card border-b border-border px-6 py-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search..."
              className="pl-9 h-9 bg-muted/50 border-border/50"
            />
          </div>
          {navLinks.map((link) => (
            <a key={link.label} href={link.href} className="block text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
              {link.label}
            </a>
          ))}
          {user && isPartnerAdmin && partnerHubOn && (
            <a href="/partner" className="block text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
              Partner Hub
            </a>
          )}
          {user ? (
            <>
              <a href="/profile" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
                <User className="h-4 w-4" />
                My Profile
              </a>
              {coachOn && (
                <a href="/coach" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
                  <Sparkles className="h-4 w-4" />
                  Assessment
                </a>
              )}
              <a href="/account" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
                <Settings className="h-4 w-4" />
                Account Settings
              </a>
              {isOwnerAdmin && (
                <a href="/admin" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
                  <ShieldCheck className="h-4 w-4" />
                  Admin Dashboard
                </a>
              )}
              <button
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2 w-full text-left"
                onClick={() => { setMobileOpen(false); signOut(); }}
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <a href="/auth" className="block text-sm text-muted-foreground hover:text-foreground py-2" onClick={() => setMobileOpen(false)}>
                Sign In
              </a>
              <Button size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold mt-2" onClick={() => { setMobileOpen(false); setSignUpOpen(true); }}>
                Get Started
              </Button>
            </>
          )}
        </div>
      )}
      {season && (
        <div
          className="h-[3px] w-full bg-season-ribbon"
          role="presentation"
          aria-label={season.label}
        />
      )}
    </header>
    </>
  );
};

export default SiteHeader;
